import isEqual from 'lodash/isEqual';
import { z } from 'zod';

import {
  zodPlaceholder,
  zodSelectOption,
  zodStringOptional,
  zodStringOptionalWithMinMax,
} from '@/shared/lib/validations';
import { CUSTOM_OPTION_ID } from '@/shared/ui';

import { FormConstants, IdConstants } from '../../../../components/Forms/constants';

export const optionalCountrySchema = z.object({
  country: zodSelectOption.nullable().optional(),
});
export const ukranianAddressBlockBaseSchema = z.object({
  region: zodPlaceholder,
  district: zodPlaceholder,
  settlement: zodPlaceholder,
  street: zodPlaceholder,
  custom_street: zodPlaceholder,
  house: zodPlaceholder,
  building: zodPlaceholder,
  apartment: zodPlaceholder,
  zip_code: zodPlaceholder,
});
export const internationalAddressBlockBaseSchema = z.object({
  region: z.null().optional(),
  district: z.null().optional(),
  settlement: zodStringOptionalWithMinMax().nullable(),
  street: zodStringOptionalWithMinMax().nullable(),
  house: zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_7 }).nullable(),
  building: zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_5 }).nullable(),
  apartment: zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_5 }).nullable(),
  zip_code: zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_50 }).nullable(),
});

export const internationalAddressBlockBaseSchemaWithoutAppartments = z.object({
  region: z.null().optional(),
  district: z.null().optional(),
  settlement: zodStringOptionalWithMinMax().nullable(),
  street: zodStringOptionalWithMinMax().nullable(),
  custom_street: z.any(),
  house: z.any(),
  building: z.any(),
  apartment: z.any(),
  zip_code: z.any(),
});

export const ukranianAddressBlockSchema = ukranianAddressBlockBaseSchema.superRefine((data, ctx) => {
  for (const key in data) {
    const fieldName = key as keyof typeof data;
    const schema = (() => {
      switch (fieldName) {
        case 'region':
          return zodSelectOption.nullable().optional();

        case 'district': {
          if (data.region?.id !== IdConstants.KYIV && data.region?.id !== IdConstants.SEVASTOPOL)
            return zodSelectOption.nullable().optional();

          return z.null({ message: 'Для міста Київ та Севастополь дане поле повинно бути порожнім' });
        }

        case 'settlement': {
          if (data.region?.id !== IdConstants.KYIV && data.region?.id !== IdConstants.SEVASTOPOL)
            return zodSelectOption.nullable().optional();

          return zodSelectOption.refine(
            (settlement) => isEqual(settlement, data.region),
            'Дане поле має бути таким самим як поле "Область"',
          );
        }

        case 'street':
          return zodSelectOption
            .extend({
              postCode: zodStringOptional.nullable(),
            })
            .nullable()
            .optional();

        case 'custom_street':
          return data.street?.id === CUSTOM_OPTION_ID ? zodStringOptionalWithMinMax().nullable() : z.any();

        case 'house':
          return zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_7 }).nullable().optional();

        case 'building':
          return zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_5 }).nullable().optional();

        case 'apartment':
          return zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_5 }).nullable().optional();

        case 'zip_code':
          return zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_50 }).nullable().optional();

        default:
          throw new Error('Missing validation schema for:', fieldName);
      }
    })();

    const { success, error } = schema.safeParse(data[fieldName]);

    if (!success) {
      for (const issue of error.issues) {
        ctx.addIssue({ ...issue, path: [fieldName] });
      }
    }
  }
});

export const ukranianAddressBlockSchemaWithoutAppartments = ukranianAddressBlockBaseSchema.superRefine((data, ctx) => {
  for (const key in data) {
    const fieldName = key as keyof typeof data;
    const schema = (() => {
      switch (fieldName) {
        case 'region':
          return zodSelectOption.nullable().optional();

        case 'district': {
          if (data.region?.id !== IdConstants.KYIV && data.region?.id !== IdConstants.SEVASTOPOL)
            return zodSelectOption.nullable().optional();

          return z.null({ message: 'Для міста Київ та Севастополь дане поле повинно бути порожнім' });
        }

        case 'settlement': {
          if (data.region?.id !== IdConstants.KYIV && data.region?.id !== IdConstants.SEVASTOPOL)
            return zodSelectOption.nullable().optional();

          return zodSelectOption.refine(
            (settlement) => isEqual(settlement, data.region),
            'Дане поле має бути таким самим як поле "Область"',
          );
        }

        case 'street':
          return zodSelectOption
            .extend({
              postCode: zodStringOptional.nullable(),
            })
            .nullable()
            .optional();

        case 'custom_street':
          return data.street?.id === CUSTOM_OPTION_ID ? zodStringOptionalWithMinMax().nullable() : z.any();

        case 'house':
          return z.any();

        case 'building':
          return z.any();

        case 'apartment':
          return z.any();

        case 'zip_code':
          return z.any();

        default:
          throw new Error('Missing validation schema for:', fieldName);
      }
    })();

    const { success, error } = schema.safeParse(data[fieldName]);

    if (!success) {
      for (const issue of error.issues) {
        ctx.addIssue({ ...issue, path: [fieldName] });
      }
    }
  }
});

export const internationalAddressBlockSchema = internationalAddressBlockBaseSchemaWithoutAppartments;

export const ukranianAddressSchema = z.intersection(optionalCountrySchema, ukranianAddressBlockSchema);
export const internationalAddressSchema = z.intersection(optionalCountrySchema, internationalAddressBlockSchema);
export const optionalAddressBlockSchema = z.union([ukranianAddressSchema, internationalAddressSchema]);
